import styled from '@emotion/styled';
import { alpha, Typography } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { MicroMutedIcon, VideoRecorderOffIcon } from 'assets/svg';
import { useAppSelector } from 'features/app/hooks';
import { isMicrophoneMutedSelector } from 'features/call/call-base/store';
import { UserVideo } from 'features/local-video/components';
import { useLocalVideoManager } from 'features/local-video/hooks';
import { isLocalVideoEnabledSelector } from 'features/local-video/store';
import { textEllipsis } from 'features/theme/commonCss';
import theme from 'features/theme/theme';
import { agentNumberSelector } from 'features/user/store';

export const LocalVideo: FC = () => {
  const { t } = useTranslation();
  const { selfViewStream, handleStalled, isPrivacyEnabled } =
    useLocalVideoManager();

  const isLocalVideoEnabled = useAppSelector(isLocalVideoEnabledSelector);
  const isUserMicrophoneMuted = useAppSelector(isMicrophoneMutedSelector);
  const agentNumber = useAppSelector(agentNumberSelector);

  if (!isLocalVideoEnabled) return null;

  return (
    <Container $isPrivacyEnabled={isPrivacyEnabled} data-testid='local-video'>
      {isPrivacyEnabled ? (
        <VideoRecorderOffIcon title='video-recorder-off-icon' />
      ) : (
        <UserVideo mediaStream={selfViewStream} handleStalled={handleStalled} />
      )}
      <UserLabel variant='caption'>
        {t('me')} ({agentNumber})
        {isUserMicrophoneMuted && <MutedIcon title='micro-muted' />}
      </UserLabel>
    </Container>
  );
};

const Container = styled('div')<{ $isPrivacyEnabled: boolean }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 266px;
  height: ${(props) => (props.$isPrivacyEnabled ? '200px' : 'auto')};
  /* TODO: get from theme when Doug updates designs */
  background-color: #222;
  border-radius: 8px;
`;

const UserLabel = styled(Typography)`
  max-width: 100%;
  position: absolute;
  display: flex;
  bottom: ${theme.spacing(8)};
  left: ${theme.spacing(7)};
  padding: ${theme.spacing(3, 4)};
  border-radius: 8px;
  background-color: ${alpha(theme.palette.common.black, 0.4)};
  color: ${theme.palette.common.white};
  ${textEllipsis}
`;

const MutedIcon = styled(MicroMutedIcon)`
  width: 16px;
  height: 16px;
  margin-left: ${theme.spacing(2)};
`;
